import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';
import { getRewards } from '../../../api/rewards.api';
import RewardCard from '../../../components/reward/card';
import HeaderList from '../../../components/reward/header-list';
import { UIReward } from '../../../types/rewards';

interface IProps {
  id: string;
  merchantName: string;
  currentTierColor?: string; // Optional prop for current tier color
}

const RewardSection: React.FC<IProps> = ({
  id,
  merchantName,
  currentTierColor
}) => {
  const {
    data: rewards,
    isPending: isLoading,
    isError
  } = useQuery({
    queryKey: ['getRewards', id],
    queryFn: () => getRewards(id || '', 'all'),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-y-4">
        <HeaderList
          title="My Rewards"
          description="Loading rewards..."
          href={`/my-rewards-hub/${id}/rewards`}
        />
        <div className="flex flex-wrap px-2">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="basis-full py-2 sm:basis-1/2 sm:pr-2 lg:basis-1/3"
            >
              <div className="h-24 animate-pulse rounded-lg bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !rewards || !rewards.success) {
    return (
      <div className="mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-y-4">
        <HeaderList
          title="My Rewards"
          description="Unable to load rewards. Please try again."
          href={`/my-rewards-hub/${id}/rewards`}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[calc(100%-2rem)] flex-col gap-y-4">
      <div className="flex flex-col gap-y-0 p-1">
        <div className="flex items-center">
          <h2 className="flex flex-1 flex-nowrap items-center text-lg font-semibold text-gray-900">
            My Rewards
          </h2>
          {!(rewards?.data?.rewards.length <= 3) && (
            <Link
              to={`/my-rewards-hub/${id}/rewards`}
              state={{ currentTierColor }}
              className="flex flex-nowrap items-center gap-x-1 text-nowrap text-sm/6 !text-gray-500 hover:text-gray-400"
            >
              See all
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.25 10.5L8.75 7L5.25 3.5"
                  stroke="currentColor"
                  strokeOpacity="0.7"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          )}
        </div>
        <p className="font-base text-xs text-gray-400">
          Redeem rewards instantly at the store when you pay with AyeFace
        </p>
      </div>
      <div className="flex flex-col gap-y-3 px-2">
        {rewards.success && rewards?.data?.rewards.length > 0 ? (
          rewards.data.rewards.slice(0, 3).map((reward: UIReward) => (
            <div key={reward.id}>
              <RewardCard
                merchantName={merchantName}
                {...reward}
                color={currentTierColor}
              />
            </div>
          ))
        ) : (
          <div className="flex h-32 items-center justify-center text-gray-400">
            No rewards available at the moment.
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardSection;
